import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CONSENT_LABELS } from './recipientsModel';

export const GroupCheckboxes = ({ groups, value, onChange }) => (
  groups.length ? (
    <div className="max-h-36 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
      {groups.map((group) => (
        <label key={group.id} className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox
            checked={value.includes(group.id)}
            onCheckedChange={(checked) => onChange(checked ? [...value, group.id] : value.filter((id) => id !== group.id))}
          />
          {group.name}
        </label>
      ))}
    </div>
  ) : <p className="text-xs text-slate-400">Geen connectgroepen beschikbaar. Koppelen is optioneel.</p>
);

export const AddUsersDialog = ({ open, onOpenChange, candidates, canSeeAll, showAll, onToggleAll, groups, onSubmit, busy }) => {
  const [selected, setSelected] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [search, setSearch] = useState('');
  useEffect(() => { if (open) { setSelected([]); setGroupIds([]); setSearch(''); } }, [open]);

  const needle = search.trim().toLowerCase();
  const visible = candidates.filter((candidate) => !needle || `${candidate.name} ${candidate.email}`.toLowerCase().includes(needle));
  const toggle = (id, checked) => setSelected((current) => (checked ? [...current, id] : current.filter((item) => item !== id)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Zoea-gebruikers toevoegen</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">
          Toevoegen aan de verzendlijst geeft deze personen geen toegang tot Connectlessen of andere modules.
        </p>
        <Input aria-label="Zoek gebruikers" placeholder="Zoek op naam of e-mailadres" value={search} onChange={(event) => setSearch(event.target.value)} />
        {canSeeAll && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={showAll} onCheckedChange={(checked) => onToggleAll(!!checked)} />
            Toon alle gebruikers (niet alleen connectleiders)
          </label>
        )}
        <div className="max-h-60 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="candidate-list">
          {visible.map((candidate) => (
            <label key={candidate.id} className={`flex items-center gap-2 rounded px-1 py-1 text-sm ${candidate.already_added ? 'opacity-50' : ''}`}>
              <Checkbox
                disabled={candidate.already_added}
                checked={selected.includes(candidate.id)}
                onCheckedChange={(checked) => toggle(candidate.id, !!checked)}
              />
              <span className="min-w-0">
                <span className="block truncate font-medium text-slate-900">{candidate.name}</span>
                <span className="block truncate text-xs text-slate-500">{candidate.email}{candidate.already_added ? ' · staat al op de lijst' : ''}</span>
              </span>
            </label>
          ))}
          {!visible.length && <p className="p-2 text-sm text-slate-500">Geen gebruikers gevonden.</p>}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Connectgroep (optioneel)</Label>
          <GroupCheckboxes groups={groups} value={groupIds} onChange={setGroupIds} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button type="button" disabled={!selected.length || busy} onClick={() => onSubmit(selected, groupIds)} className="bg-fuchsia-600 text-white hover:bg-fuchsia-700">
            {selected.length ? `${selected.length} toevoegen` : 'Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RecipientFormDialog = ({ open, onOpenChange, recipient, groups, onSubmit, busy }) => {
  const editing = Boolean(recipient);
  const isUser = recipient?.kind === 'user';
  const [form, setForm] = useState({ name: '', email: '', group_ids: [], email_consent: 'unknown' });
  useEffect(() => {
    if (!open) return;
    setForm(recipient
      ? { name: recipient.name || '', email: recipient.email || '', group_ids: recipient.group_ids || [], email_consent: recipient.email_consent || 'unknown' }
      : { name: '', email: '', group_ids: [], email_consent: 'unknown' });
  }, [open, recipient]);

  const submit = (event) => {
    event.preventDefault();
    if (!isUser && (!form.name.trim() || !form.email.trim())) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader><DialogTitle>{editing ? 'Ontvanger bewerken' : 'Externe ontvanger toevoegen'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {isUser ? (
            <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
              {recipient.name} · {recipient.email}<br />
              Naam en e-mailadres komen uit het Zoea-account en worden daar gewijzigd.
            </p>
          ) : (
            <>
              <div className="space-y-1"><Label htmlFor="recipient-name">Naam *</Label><Input id="recipient-name" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></div>
              <div className="space-y-1"><Label htmlFor="recipient-email">E-mailadres *</Label><Input id="recipient-email" type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></div>
              {editing && form.email !== recipient.email && (
                <p className="text-xs text-amber-700">Bij een ander e-mailadres vervalt de eerder vastgelegde toestemming.</p>
              )}
            </>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Connectgroep (optioneel)</Label>
            <GroupCheckboxes groups={groups} value={form.group_ids} onChange={(group_ids) => setForm({ ...form, group_ids })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="recipient-consent" className="text-xs">E-mailtoestemming</Label>
            <select id="recipient-consent" className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.email_consent} onChange={(event) => setForm({ ...form, email_consent: event.target.value })}>
              {Object.entries(CONSENT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={busy} className="bg-fuchsia-600 text-white hover:bg-fuchsia-700">{editing ? 'Opslaan' : 'Toevoegen'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
